// Browser-side input event capture for the student view.
//
// Two operating modes selected by `coarsePointer`:
//
//  - DESKTOP (mouse / pen-precise): every pointerdown/up/move maps directly
//    to a CDP mousePressed/Released/Moved.
//
//  - MOBILE / TOUCH: behaviour mimics actual web browsing:
//    * single-finger tap (small movement, short duration) → synthesised
//      `down`+`up` pair on `pointerup`
//    * single-finger drag (movement past threshold) → wheel events scrolling
//      the operator's Chromium page (this is what feels native — finger drag
//      = scroll page, not "drag mouse")
//    * Soft keyboard / IME: caller wires up a hidden focus target so the
//      OS keyboard pops on tap, then routes keydown/keyup/composition events
//      from that focus target through us via `attachKeyboardSource`.
//
// All events are encoded into the same `InputEvent` shape so the operator
// CDP replay path is unchanged.

export type InputEvent =
  | { kind: 'mouse'; type: 'down' | 'up' | 'move'; xPct: number; yPct: number; button: 'left' | 'right' | 'middle'; buttons: number; modifiers: number; }
  | { kind: 'wheel'; xPct: number; yPct: number; deltaX: number; deltaY: number; modifiers: number; }
  | { kind: 'key'; type: 'down' | 'up'; key: string; code: string; modifiers: number; text?: string; }
  | { kind: 'text'; text: string; }
  | { kind: 'ime'; phase: 'start' | 'update' | 'commit'; text: string; selectionStart?: number; selectionEnd?: number; };

const MODS = { Alt: 1, Ctrl: 2, Meta: 4, Shift: 8 };

export function modBitfield(ev: { altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }): number {
  let m = 0;
  if (ev.altKey)   m |= MODS.Alt;
  if (ev.ctrlKey)  m |= MODS.Ctrl;
  if (ev.metaKey)  m |= MODS.Meta;
  if (ev.shiftKey) m |= MODS.Shift;
  return m;
}

export function buttonName(b: number): 'left' | 'right' | 'middle' {
  if (b === 1) return 'middle';
  if (b === 2) return 'right';
  return 'left';
}

export interface CaptureOptions {
  /** Hidden input element used to summon the OS soft keyboard + receive IME. */
  keyboardTarget?: HTMLInputElement | HTMLTextAreaElement;
  /** Override touch detection (defaults to `(pointer: coarse)` matchMedia). */
  coarsePointer?: boolean;
}

export interface InputCaptureHandle {
  destroy(): void;
}

const TAP_MOVE_THRESHOLD_PX = 8;
const TAP_MAX_DURATION_MS = 350;
const SCROLL_GESTURE_THRESHOLD_PX = 6;

interface TouchState {
  startX: number;
  startY: number;
  startT: number;
  lastX: number;
  lastY: number;
  scrolling: boolean;
  pointerType: string;
}

export function captureInput(
  el: HTMLElement,
  videoEl: HTMLVideoElement,
  send: (ev: InputEvent) => void,
  opts: CaptureOptions = {},
): InputCaptureHandle {
  const coarse = opts.coarsePointer ?? (typeof window !== 'undefined'
    ? (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
    : false);

  function mapCoords(clientX: number, clientY: number): { xPct: number; yPct: number } | null {
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
    const vw = videoEl.videoWidth || rect.width;
    const vh = videoEl.videoHeight || rect.height;
    const scale = Math.min(rect.width / vw, rect.height / vh);
    const dispW = vw * scale;
    const dispH = vh * scale;
    const offX = (rect.width - dispW) / 2;
    const offY = (rect.height - dispH) / 2;
    const ix = (x - offX) / dispW;
    const iy = (y - offY) / dispH;
    if (ix < 0 || ix > 1 || iy < 0 || iy > 1) return null;
    return { xPct: ix, yPct: iy };
  }

  function fire(ev: InputEvent) { try { send(ev); } catch (_) {} }

  const touches = new Map<number, TouchState>();

  function onPointerDown(e: PointerEvent) {
    const c = mapCoords(e.clientX, e.clientY);
    if (!c) return;

    if (coarse && (e.pointerType === 'touch' || e.pointerType === 'pen')) {
      // Mobile: don't emit mouse-down yet. Wait until pointerup to decide
      // whether it was a tap or a scroll.
      // We DON'T call .focus() programmatically here — when the keyboardTarget
      // input IS the capture layer, iOS natively focuses it on tap (and not
      // on scroll/swipe), so the soft keyboard appears only when expected.
      touches.set(e.pointerId, {
        startX: e.clientX, startY: e.clientY,
        startT: performance.now(),
        lastX: e.clientX, lastY: e.clientY,
        scrolling: false,
        pointerType: e.pointerType,
      });
      return;
    }

    // Desktop: down → CDP mousePressed.
    e.preventDefault();
    fire({
      kind: 'mouse', type: 'down',
      xPct: c.xPct, yPct: c.yPct,
      button: buttonName(e.button),
      buttons: e.buttons, modifiers: modBitfield(e),
    });
  }

  function onPointerMove(e: PointerEvent) {
    const t = touches.get(e.pointerId);
    if (t) {
      const dx = e.clientX - t.lastX;
      const dy = e.clientY - t.lastY;
      t.lastX = e.clientX; t.lastY = e.clientY;
      const totalDx = e.clientX - t.startX;
      const totalDy = e.clientY - t.startY;
      const totalDist = Math.hypot(totalDx, totalDy);
      if (!t.scrolling && totalDist >= SCROLL_GESTURE_THRESHOLD_PX) {
        t.scrolling = true;
      }
      if (t.scrolling) {
        e.preventDefault();
        const c = mapCoords(e.clientX, e.clientY);
        if (!c) return;
        // Wheel deltas are inverse of finger drag direction.
        fire({
          kind: 'wheel',
          xPct: c.xPct, yPct: c.yPct,
          deltaX: -dx, deltaY: -dy,
          modifiers: modBitfield(e),
        });
      }
      return;
    }
    // Desktop pointermove (no down required for hover replay).
    const c = mapCoords(e.clientX, e.clientY);
    if (!c) return;
    fire({
      kind: 'mouse', type: 'move',
      xPct: c.xPct, yPct: c.yPct,
      button: buttonName(e.button), buttons: e.buttons,
      modifiers: modBitfield(e),
    });
  }

  function onPointerUp(e: PointerEvent) {
    const t = touches.get(e.pointerId);
    touches.delete(e.pointerId);
    if (t) {
      const totalDx = e.clientX - t.startX;
      const totalDy = e.clientY - t.startY;
      const totalDist = Math.hypot(totalDx, totalDy);
      const dur = performance.now() - t.startT;
      if (!t.scrolling && totalDist <= TAP_MOVE_THRESHOLD_PX && dur <= TAP_MAX_DURATION_MS) {
        // Synthesised tap → mouse down + up at start coordinates. (Focus
        // already happened in onPointerDown to be safely inside the
        // user-gesture window.)
        const c = mapCoords(t.startX, t.startY);
        if (c) {
          fire({ kind: 'mouse', type: 'down', xPct: c.xPct, yPct: c.yPct, button: 'left', buttons: 1, modifiers: 0 });
          fire({ kind: 'mouse', type: 'up',   xPct: c.xPct, yPct: c.yPct, button: 'left', buttons: 0, modifiers: 0 });
        }
      }
      return;
    }
    e.preventDefault();
    const c = mapCoords(e.clientX, e.clientY);
    if (!c) return;
    fire({
      kind: 'mouse', type: 'up',
      xPct: c.xPct, yPct: c.yPct,
      button: buttonName(e.button), buttons: e.buttons,
      modifiers: modBitfield(e),
    });
  }

  function onPointerCancel(e: PointerEvent) { touches.delete(e.pointerId); }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const c = mapCoords(e.clientX, e.clientY);
    if (!c) return;
    fire({
      kind: 'wheel',
      xPct: c.xPct, yPct: c.yPct,
      deltaX: e.deltaX, deltaY: e.deltaY,
      modifiers: modBitfield(e),
    });
  }

  // Keyboard / IME: bind to either the element itself OR the hidden
  // keyboard target (so soft keyboards on iOS/Android summon properly).
  const keyTarget: HTMLElement = opts.keyboardTarget || el;

  let composing = false;

  const onCompositionStart = (e: CompositionEvent) => { composing = true; fire({ kind: 'ime', phase: 'start', text: e.data || '' }); };
  const onCompositionUpdate = (e: CompositionEvent) => { fire({ kind: 'ime', phase: 'update', text: e.data || '' }); };
  const onCompositionEnd = (e: CompositionEvent) => {
    composing = false;
    fire({ kind: 'ime', phase: 'commit', text: e.data || '' });
    // Reset the hidden input so it doesn't accumulate text.
    if (opts.keyboardTarget) opts.keyboardTarget.value = '';
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (composing || (e as any).isComposing) return;
    // Skip printable single characters — onInput handles them so we don't
    // double-insert (would produce 'aa' for one 'a' tap).
    if (e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) return;
    fire({
      kind: 'key', type: 'down',
      key: e.key, code: e.code,
      modifiers: modBitfield(e),
    });
  };
  const onKeyUp = (e: KeyboardEvent) => {
    if (composing || (e as any).isComposing) return;
    if (e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) return;
    fire({ kind: 'key', type: 'up', key: e.key, code: e.code, modifiers: modBitfield(e) });
  };

  // The `input` event on the hidden field fires on every soft-keyboard insertion
  // (autocorrect, swipe-typing, voice input). Forward those as `text` events
  // so they reach the operator faithfully — they're often the only signal on
  // mobile where keydown is unreliable.
  const onInput = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) return;
    // The DOM event subclass — our local 'InputEvent' is a wire-format type
    // alias so we cast through unknown.
    const ie = (e as unknown) as { inputType?: string };
    const t = ie.inputType || '';
    if (t.startsWith('delete')) {
      // iOS soft keyboard backspace. Forward as Backspace key.
      fire({ kind: 'key', type: 'down', key: 'Backspace', code: 'Backspace', modifiers: 0 });
      fire({ kind: 'key', type: 'up',   key: 'Backspace', code: 'Backspace', modifiers: 0 });
      target.value = '';
      return;
    }
    if (composing) return;
    const v = target.value;
    if (v) {
      fire({ kind: 'text', text: v });
      target.value = '';
    }
  };

  const onContext = (e: Event) => e.preventDefault();
  // Swallow the click event after a tap. Without this, iOS Safari may
  // interpret the click on the capture layer as 'user clicked outside the
  // focused input' and dismiss the soft keyboard.
  const onClick = (e: Event) => { e.preventDefault(); e.stopPropagation(); };

  el.addEventListener('pointerdown',   onPointerDown,   { passive: false });
  el.addEventListener('pointermove',   onPointerMove,   { passive: false });
  el.addEventListener('pointerup',     onPointerUp,     { passive: false });
  el.addEventListener('pointercancel', onPointerCancel, { passive: true  });
  el.addEventListener('wheel',         onWheel,         { passive: false });
  el.addEventListener('contextmenu',   onContext);
  el.addEventListener('click',         onClick);

  keyTarget.addEventListener('keydown', onKeyDown);
  keyTarget.addEventListener('keyup',   onKeyUp);
  keyTarget.addEventListener('compositionstart',  onCompositionStart);
  keyTarget.addEventListener('compositionupdate', onCompositionUpdate);
  keyTarget.addEventListener('compositionend',    onCompositionEnd);
  if (opts.keyboardTarget) keyTarget.addEventListener('input', onInput as EventListener);

  return {
    destroy() {
      el.removeEventListener('pointerdown',   onPointerDown);
      el.removeEventListener('pointermove',   onPointerMove);
      el.removeEventListener('pointerup',     onPointerUp);
      el.removeEventListener('pointercancel', onPointerCancel);
      el.removeEventListener('wheel',         onWheel);
      el.removeEventListener('contextmenu',   onContext);
      el.removeEventListener('click', onClick);
      keyTarget.removeEventListener('keydown', onKeyDown);
      keyTarget.removeEventListener('keyup',   onKeyUp);
      keyTarget.removeEventListener('compositionstart',  onCompositionStart);
      keyTarget.removeEventListener('compositionupdate', onCompositionUpdate);
      keyTarget.removeEventListener('compositionend',    onCompositionEnd);
      if (opts.keyboardTarget) keyTarget.removeEventListener('input', onInput as EventListener);
    },
  };
}
